import { css } from '@emotion/react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { EmptySection } from '../../../components/EmptySection';
import { CompanySearch } from '../../../components/CompanySearch/CompanySearch';
import { CompanySearchResponseWithRealId } from '../../../schemas/CompanySearchResponse.schema';
import { CompanyDealDocsResponse, getAllCompanyDealDocs } from '../../../services/queries/DealDocQueries';
import { fetchCompanyRaw } from '../../../services/queries/MaggieCompanyQueries';
import { LoadingStatus } from '../../../types';
import { useAsync } from '../../../util/hook-utils';
import { ReassignDocumentModal } from './ReassignDocumentModal';
import { DealDocsGrid } from './DealDocsGrid';
import { invalidateDataState, selectedCompanyIdState } from './updateDealDocsState';

export function UpdateDealDocsRoundLoader() {
  const [selectedCompanyId, setSelectedCompanyId] = useAtom(selectedCompanyIdState);
  const invalidate = useAtomValue(invalidateDataState);
  const [companyName, setCompanyName] = useState<string | null>(null);

  // Reset selection when tab becomes hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setSelectedCompanyId(null);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [setSelectedCompanyId]);

  // Fetch company name and update tab title
  useEffect(() => {
    const originalTitle = document.title;

    if (selectedCompanyId) {
      fetchCompanyRaw(selectedCompanyId)
        .then((company) => {
          setCompanyName(company.name);
          document.title = `${company.name} - Update Deal Docs`;
        })
        .catch((error) => {
          console.error('Failed to fetch company name:', error);
          setCompanyName(null);
          document.title = 'Update Deal Docs';
        });
    } else {
      setCompanyName(null);
      document.title = 'Update Deal Docs';
    }

    return () => {
      document.title = originalTitle;
    };
  }, [selectedCompanyId]);

  const fetchDocuments = useCallback(async (): Promise<CompanyDealDocsResponse | null> => {
    if (!selectedCompanyId) {
      return null;
    }
    return await getAllCompanyDealDocs(selectedCompanyId);
  }, [selectedCompanyId]);

  const { data, loading, error } = useAsync(fetchDocuments, {
    id: `${selectedCompanyId}-${invalidate}`,
  });

  if (!selectedCompanyId) {
    return (
      <Box width='100%' height='100%' padding='8px 12px 12px'>
        <ReassignDocumentModal />
        <UpdateDealDocsRoundPresenter
          data={null}
          loading={LoadingStatus.idle}
          error={null}
          selectedCompanyId={selectedCompanyId}
          companyName={companyName}
          onCompanyChange={setSelectedCompanyId}
        />
      </Box>
    );
  }

  return (
    <Box width='100%' height='100%' padding='8px 12px 12px'>
      <ReassignDocumentModal />
      <UpdateDealDocsRoundPresenter
        data={data}
        loading={loading}
        error={error}
        selectedCompanyId={selectedCompanyId}
        companyName={companyName}
        onCompanyChange={setSelectedCompanyId}
      />
    </Box>
  );
}

interface UpdateDealDocsRoundPresenterProps {
  data: CompanyDealDocsResponse | null;
  loading: LoadingStatus;
  error: string | null;
  selectedCompanyId: number | null;
  companyName: string | null;
  onCompanyChange: (companyId: number | null) => void;
}

function UpdateDealDocsRoundPresenter({
  data,
  loading,
  error,
  selectedCompanyId,
  companyName,
  onCompanyChange,
}: UpdateDealDocsRoundPresenterProps) {
  return (
    <Typography color='text.primary' component='section' width='100%'>
      <Stack spacing={2}>
        <Stack direction='row' alignItems='center' spacing={2}>
          <Typography variant='h4'>Update Round for Deal Docs</Typography>
          {companyName && (
            <Typography variant='h4' color='primary.main'>
              - {companyName}
            </Typography>
          )}
        </Stack>

        <CompanySelector selectedCompanyId={selectedCompanyId} onCompanyChange={onCompanyChange} />

        {loading === LoadingStatus.loading && <Typography>Loading documents...</Typography>}
        {error && <Alert severity='error'>Failed to load documents: {error}</Alert>}

        {!selectedCompanyId && loading !== LoadingStatus.loading && !error && (
          <EmptySection
            title='Select a Company'
            message='Search for a portfolio company above to view and manage its deal documents'
          />
        )}

        {data && selectedCompanyId && (
          <Stack spacing={3}>
            {/* Company-Level Documents */}
            <Box>
              <Typography variant='h5' mb={2}>
                Company-Level Documents ({data.companyDocs.length})
              </Typography>
              {data.companyDocs.length === 0 ? (
                <Typography color='text.secondary'>No company-level documents</Typography>
              ) : (
                <DealDocsGrid documents={data.companyDocs} />
              )}
            </Box>

            {/* Round Documents */}
            {data.roundDocs.map((roundData) => (
              <Box key={roundData.roundId}>
                <Typography variant='h5' mb={2}>
                  {roundData.roundName}
                  {roundData.roundDate && ` - ${new Date(roundData.roundDate).toLocaleDateString()}`} (
                  {roundData.documents.length} {roundData.documents.length === 1 ? 'document' : 'documents'})
                </Typography>
                {roundData.documents.length === 0 ? (
                  <Typography color='text.secondary'>No documents for this round</Typography>
                ) : (
                  <DealDocsGrid documents={roundData.documents} />
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </Typography>
  );
}

interface CompanySelectorProps {
  selectedCompanyId: number | null;
  onCompanyChange: (companyId: number | null) => void;
}

const maxWidth500 = css`
  max-width: 500px;
`;

function CompanySelector({ selectedCompanyId, onCompanyChange }: CompanySelectorProps) {
  const handleCompanyChange = (company: CompanySearchResponseWithRealId) => {
    onCompanyChange(company.id);
  };

  return (
    <Box css={maxWidth500}>
      <CompanySearch
        key={selectedCompanyId ?? 'no-selection'}
        showPortcoOnly={true}
        showSelectedOption={true}
        fullWidth={true}
        onChange={handleCompanyChange}
      />
    </Box>
  );
}
